import moment from 'moment';
import { ConfigConstants } from '../config/config-constants';

export class TimeUtilities {
    /**
     * Transforms an OData date format and a XSD duration into a moment date.
     * Will return null if dateString is empty or null. Is able to work with an empty or null timeString.
     * Any occurneces of "(null)" in the timeString will be replaced automatically.
     *
     * @param dateString - Odata date format e.g. /Date(123456789)/
     * @param timeString - XSD time duration e.g. PT14H30M00S
     * @param isUTC - to determine whether the dateString is in UTC timezone and should be converted accordingly
     * @returns {moment.Moment|null}
     */
    public static momentFromDateAndTime(dateString: string, timeString: string, isUTC = false): moment.Moment {
        let res = moment.invalid();
        let date = moment.invalid();
        let time = moment.duration(0);

        // dateString can be null and that's ok.
        if (dateString) {
            const utcDate = moment.utc(dateString);

            if (isUTC) {
                date = utcDate;
            } else {
                // when -hours timezone is used, moment(dateString) will return the wrong date
                // so setting day, month and year manually, will keep date in every timezone the same
                date = moment().year(utcDate.year()).month(utcDate.month()).date(utcDate.date());
            }

            if (!date.isValid()) {
                throw new Error('TimeUtilities.momentFromDateAndTime - Date string is invalid: ' + dateString);
            }
            date = date.startOf('day');

            // Store the date in the result. If timeString is empty we have at least the date now.
            res = date;
        }

        if (timeString) {
            timeString = TimeUtilities.replaceNullWithZeroDigits(timeString);
            time = moment.duration(timeString);
            if (!moment.isDuration(time)) {
                throw new Error('TimeUtilities.momentFromDateAndTime - Time duration is invalid: ' + timeString);
            }
        }

        if (date.isValid()) {
            // we should have a date already, if not we want to return null.
            // if we have a time too, add it to the date and overwrite the result again
            res = date.add(time, 'milliseconds');
        }

        return res;
    }

    /**
     * Searches for occurences of "(null)" in an string and replaces it with two zero digits.
     * Mainly used to "repair" XSD time durations.
     *
     * @param timeValue - XSD time duration e.g. PT14H30M00S
     * @returns {string}
     */
    public static replaceNullWithZeroDigits(timeValue: string): string {
        return timeValue.replace(/\(null\)/g, '00');
    }

    /**
     * Returns if the time spans described by two pairs of start and end times overlap
     *
     * @param startA
     * @param endA
     * @param startB
     * @param endB
     */
    public static hasOverlap(
        startA: moment.Moment,
        endA: moment.Moment,
        startB: moment.Moment,
        endB: moment.Moment,
    ): boolean {
        return startA.isBefore(endB) && endA.isAfter(startB);
    }

    /**
     * This function derives the next working day based on the current day. (if today is Friday, then it should default the next Monday)
     *
     * @param time
     * @param businessDays
     * @param isUTC
     */
    public static getNextWorkingDayDateTime(time: moment.Moment, businessDays = 1, isUTC = false): moment.Moment {
        const today = isUTC ? moment.utc() : moment();
        let nextWorkingDay = today.add(businessDays, 'days');
        const isoSaturday = 6;
        const isoSunday = 7;
        while (nextWorkingDay.isoWeekday() === isoSaturday || nextWorkingDay.isoWeekday() === isoSunday) {
            nextWorkingDay = nextWorkingDay.add(1, 'days');
        }
        nextWorkingDay = nextWorkingDay.set({
            hour: time.get('hour'),
            minute: time.get('minute'),
            second: time.get('second'),
            millisecond: time.get('millisecond'),
        });
        return nextWorkingDay;
    }

    /**
     * Sets the time portion of a moment object to the time given in a HTML5 formatted time string
     *
     * @param target
     * @param time
     */
    public static setTimeHtml5(target: moment.Moment, time: string): moment.Moment {
        return TimeUtilities.setTimeMoment(target, moment(time, moment.HTML5_FMT.TIME_SECONDS));
    }

    /**
     * Sets the time portion of a moment object based on a source moment object
     *
     * @param target
     * @param time
     */
    public static setTimeMoment(target: moment.Moment, time: moment.Moment): moment.Moment {
        const timeObject = time.toObject();
        if (!target.isValid()) {
            target = moment();
        }

        target.set({
            hours: timeObject.hours,
            minutes: timeObject.minutes,
            seconds: timeObject.seconds,
            milliseconds: timeObject.milliseconds,
        });
        return target;
    }

    public static convertTimeFrom12hoursTo24hoursFormat(timeIn12hFormat: string): string {
        const [time, modifier] = timeIn12hFormat.split(' ');
        const timeData = time.split(':');

        let [hours] = timeData;
        const [, minutes] = timeData;

        if (hours === '12') {
            hours = '00';
        }

        const twelve = 12;
        if (modifier === 'PM') {
            hours = (parseInt(hours, 10) + twelve).toString();
        }
        return `${hours}:${minutes}`;
    }

    public static getFormattedDuration(hours: number, minutes: number): string {
        const newDuration = moment.duration(minutes, 'minutes');
        newDuration.add(hours, 'hours');

        return newDuration.format(ConfigConstants.formatPatternTimeGeneric, { trim: false });
    }
}
